import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Circle, ArrowRight } from 'lucide-react';
import type { Job } from '../lib/store';
import { initials } from '../lib/utils';

const WORKERS = ['Sammy', 'Jim', 'Stewart'];

export default function TeamStatus({ jobs, today }: { jobs: Job[]; today: string }) {
  const perWorker = useMemo(() => {
    return WORKERS.map((name) => {
      const mine = jobs.filter(
        (j) =>
          j.assigned_to?.toLowerCase() === name.toLowerCase() && j.status !== 'Invoiced'
      );
      const todayJobs = mine.filter((j) => j.date === today);
      const inProgress = todayJobs.find((j) => j.status === 'In Progress');
      const upcoming = mine.filter((j) => j.date > today).length;
      const status =
        inProgress ? 'On a job' : todayJobs.length ? 'Scheduled' : 'Free';
      const statusColor =
        status === 'On a job'
          ? 'bg-emerald-500'
          : status === 'Scheduled'
          ? 'bg-amber-500'
          : 'bg-slate-300';
      return { name, todayJobs, inProgress, upcoming, status, statusColor };
    });
  }, [jobs, today]);

  return (
    <div className="card p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-slate-900">Team today</h3>
        <span className="text-xs font-semibold text-slate-400">{WORKERS.length} people</span>
      </div>
      <div className="space-y-3">
        {perWorker.map((w) => (
          <div
            key={w.name}
            className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:border-brand-200 hover:bg-brand-50/40 transition group"
          >
            <div className="w-10 h-10 rounded-xl bg-brand-100 text-brand-700 font-bold grid place-items-center text-sm shrink-0">
              {initials(w.name)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-900">{w.name}</span>
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                  <Circle className={`h-2 w-2 fill-current ${w.statusColor.replace('bg-', 'text-')}`} />
                  {w.status}
                </span>
              </div>
              {w.inProgress ? (
                <Link
                  to={`/jobs/${w.inProgress.id}`}
                  className="text-xs text-slate-500 truncate block group-hover:text-brand-700 mt-0.5"
                >
                  {w.inProgress.title}
                </Link>
              ) : (
                <div className="text-xs text-slate-500 mt-0.5">
                  {w.todayJobs.length
                    ? `${w.todayJobs.length} job${w.todayJobs.length > 1 ? 's' : ''} today`
                    : w.upcoming
                    ? `Next in ${w.upcoming} job${w.upcoming > 1 ? 's' : ''}`
                    : 'Nothing booked'}
                </div>
              )}
            </div>
            <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-brand-600 group-hover:translate-x-0.5 transition" />
          </div>
        ))}
      </div>
    </div>
  );
}
